 
import { Box, Stack, Typography, useTheme } from '@mui/joy';
import {
	DOLLAR_FORMATTER,
	PERCENT_FORMATTER,
	SM_PERCENT_FORMATTER
} from '../../lib/format';
import { scaleLinear } from 'd3-scale';
import { max, min } from 'd3-array';
import UiTooltip from '../Controls/UiTooltip';
import ChartTooltip from './ChartTooltip';
//import { useControls } from '../../providers/ControlProvider';
import { toTitleCase } from '../../lib/utils';
import { useRoute } from '../../providers/RouteProvider';
import { useChartSectionData } from '../../providers/ChartSectionProvider';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useYear } from '../../providers/YearProvider';

interface IProps {
	type: string;
	reorder?: true;
	office?: string;
	topic: string;
	page?: string;
	chartData: {
		group: string;
		partyLabel: string;
		label: string;
		value: number;
		numCand: number;
		color: string;
		height: string;
	}[];
	print?: boolean;
}
const DisaggChart = ({
	type,
	reorder,
	office,
	topic,
	page = 'state',
	chartData,
	print = false
}: IProps) => {
	const theme = useTheme();
	const { year } = useYear();
	const { selectedState, election, congressTab, raceFilter } = useRoute();
	const { maxInParty } = useChartSectionData();
	const isFiltered =
		raceFilter[0] === 'true' || election === 'congress' || page === 'national';
	//split rep/dem data to can align bar between charts.
	const rep: { [x: string]: string | number }[] = chartData?.filter(
		({ partyLabel }) => partyLabel === 'GOP'
	);
	const dem: { [x: string]: string | number }[] = chartData?.filter(
		({ partyLabel }) => partyLabel === 'DEM'
	);
	const ind: { [x: string]: string | number }[] = chartData?.filter(
		({ partyLabel }) => partyLabel === 'IND'
	);
	 
	const maxValue = chartData ? max(chartData?.map((item) => item.value)) : null;
	 
	const scale: () => number = scaleLinear()
		 
		.domain([0, maxValue])
		 
		.range([0, 100]);

	const title =
		election === 'state' && page === 'state'
			? office
			: congressTab === 0
			? 'US Senate'
			: 'US House';

	//const num = 2; //num of groups to show in template
	const spacer = reorder ? 32 : 50;
	//reorder tells chart to put ind in center for radial chart sections vs at bottom for bar chart section
	//it also serves as flag for if radial or bar

	const formatValue = (value: number) => {
		if (SM_PERCENT_FORMATTER(value) === '0.00%') return '0%';
		else return SM_PERCENT_FORMATTER(value);
	};
	//console.log('test', SM_PERCENT_FORMATTER(0.000005));
	const matches = useMediaQuery(theme.breakpoints.up('md'));

	return (
		<>
			<Box
				sx={{
					flex: 1,
					margin: '1.75rem 1rem 1rem 0',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center'
				}}
			>
				<Box>
					{page === 'state' && election === 'state' ? (
						<Typography
							level='subgroupTitle'
							sx={{ height: '4rem', lineHeight: '1.75rem' }}
						>
							{toTitleCase(title as string)}
						</Typography>
					) : null}
					{selectedState === 'mi' && title === 'State Senate' && year === 2024 ? (
						<Typography level='annotation' sx={{ fontStyle: 'italic' }}>
							In Michigan, no State Senate races are scheduled for 2024.
						</Typography>
					) : (
						<Typography
							level='label'
							sx={{
								fontSize: '20px',
								fontWeight: 500,
								mt:
									election === 'congress' || page === 'national' ? '2.7rem' : 0
							}}
						>
							Disaggregated by women&rsquo;s race/ethnicity
						</Typography>
					)}
					{chartData && !chartData.length && selectedState !== 'mi' ? ( //&& title !== 'State Senate'
						<Typography
							level='annotation'
							sx={{ fontStyle: 'italic', mt: '1rem' }}
						>
							No data to display
						</Typography>
					) : null}

					<Box>
						{dem && dem.length ? (
							<Box
								sx={{
									height:
										(election === 'congress' || page === 'national') && !reorder
											?  
											  `${min([maxInParty.dems + 1, 3]) * spacer}px`
											: election === 'congress' || page === 'national'
											? 'unset'
											: `${maxInParty.dems * spacer}px`,
									transition: 'all 1s ease',
									mb: '2rem',
									mt: '1rem'
								}}
							>
								{dem?.map((item) => (
									<UiTooltip
										key={item.group}
										title={
											<ChartTooltip
												titleText={title as string}
												d={item as { [x: string]: string | number }}
												topic={topic}
												isFiltered={isFiltered}
											/>
										}
										variant='plain'
										sx={{ borderRadius: '0px' }}
										arrow
										placement={!matches ? 'top' : 'left'}
									>
										<Stack
											key={item.group}
											direction={'row'}
											sx={{ lineHeight: 1.3 }}
											alignItems='center'
										>
											{type === 'money' ? (
												<BoxChart
													item={item}
													scale={scale}
													topic={topic}
													isFiltered={isFiltered}
													title={title as string}
													print={print}
												/>
											) : (
												<BoxPctChart
													item={item}
													topic={topic}
													isFiltered={isFiltered}
													title={title as string}
													print={print}
												/>
											)}

											<Typography
												level='label'
												sx={{
													fontFamily: 'Source Sans Pro',
													fontSize: '20px',
													fontWeight: 700,
													color: item.color,
													lineHeight: '33.6px'
												}}
											>
												{type === 'money'
													? DOLLAR_FORMATTER(+item.value)
													: +item.value < 0.01 && +item.value > 0
													? formatValue(+item.value)
													: PERCENT_FORMATTER(+item.value)}
											</Typography>
											<Typography
												level='label'
												sx={{
													fontFamily: 'Source Sans Pro',
													fontSize: '20px',
													color: item.color,
													lineHeight: '33.6px'
												}}
											>
												&nbsp;
												{item.label}
											</Typography>
										</Stack>
									</UiTooltip>
								))}
							</Box>
						) : (
							<Box sx={{ marginTop: '1rem' }} />
						)}

						{reorder && ind && ind.length ? (
							<Box
								sx={{
									//height: `${numDem * 64}px`,
									transition: 'all 1s ease',
									mb: '2rem'
								}}
							>
								{ind.map((item) => (
									<UiTooltip
										key={item.group}
										title={
											<ChartTooltip
												titleText={title as string}
												d={item as { [x: string]: string | number }}
												topic={topic}
												isFiltered={isFiltered}
											/>
										}
										variant='plain'
										sx={{ borderRadius: '0px' }}
										arrow
										placement={!matches ? 'top' : 'left'}
									>
										<Stack
											key={item.group}
											direction={'row'}
											sx={{ lineHeight: 1.3 }}
											alignItems='center'
										>
											{type === 'money' ? (
												<BoxChart
													item={item}
													scale={scale}
													topic={topic}
													isFiltered={isFiltered}
													title={title as string}
													print={print}
												/>
											) : (
												<BoxPctChart
													item={item}
													topic={topic}
													isFiltered={isFiltered}
													title={title as string}
													print={print}
												/>
											)}
											<Typography
												level='label'
												sx={{
													fontSize: '20px',
													fontWeight: 700,
													color: item.color,
													lineHeight: '33.6px'
												}}
											>
												{type === 'money'
													? DOLLAR_FORMATTER(+item.value)
													: +item.value < 0.01 && +item.value > 0
													? formatValue(+item.value)
													: PERCENT_FORMATTER(+item.value)}
											</Typography>
											<Typography
												level='label'
												sx={{
													fontSize: '20px',
													color: item.color,
													lineHeight: '33.6px'
												}}
											>
												&nbsp;
												{item.label}
											</Typography>
										</Stack>
									</UiTooltip>
								))}
							</Box>
						) : (
							<Box sx={{ marginTop: '1rem' }} />
						)}

						{rep && rep.length ? (
							<Box
								sx={{
									mt: '.5rem',
									mb: '2rem',
									height: reorder
										? 'unset'
										: election === 'congress' || page === 'national'
										?  
										  `${min([maxInParty.dems + 1, 3]) * spacer}px`
										: `${maxInParty.reps * spacer}px`,
									transition: 'all 1s ease'
								}}
							>
								{rep.map((item) => (
									<UiTooltip
										key={item.group}
										title={
											<ChartTooltip
												titleText={title as string}
												d={item as { [x: string]: string | number }}
												topic={topic}
												isFiltered={isFiltered}
											/>
										}
										variant='plain'
										sx={{ borderRadius: '0px' }}
										arrow
										placement={!matches ? 'top' : 'left'}
									>
										<Stack
											key={item.group}
											direction={'row'}
											sx={{ lineHeight: 1.3, alignItems: 'center' }}
										>
											{type === 'money' ? (
												<BoxChart
													item={item}
													scale={scale}
													topic={topic}
													isFiltered={isFiltered}
													title={title as string}
													print={print}
												/>
											) : (
												<BoxPctChart
													item={item}
													topic={topic}
													isFiltered={isFiltered}
													title={title as string}
													print={print}
												/>
											)}

											<Typography
												level='label'
												sx={{
													fontSize: '20px',
													fontWeight: 700,
													color: item.color,
													lineHeight: '33.6px'
												}}
											>
												{type === 'money'
													? DOLLAR_FORMATTER(+item.value)
													: +item.value < 0.01 && +item.value > 0
													? formatValue(+item.value)
													: PERCENT_FORMATTER(+item.value)}
											</Typography>
											<Typography
												level='label'
												sx={{
													fontSize: '20px',
													color: item.color,
													lineHeight: '33.6px'
												}}
											>
												&nbsp;
												{item.label}
											</Typography>
										</Stack>
									</UiTooltip>
								))}
							</Box>
						) : null}
						{!reorder && ind && ind.length ? (
							<Box
								sx={{
									//height: `${numDem * 64}px`,
									transition: 'all 1s ease',
									mb: '2rem'
								}}
							>
								{ind.map((item) => (
									<UiTooltip
										key={item.group}
										title={
											<ChartTooltip
												titleText={title as string}
												d={item as { [x: string]: string | number }}
												topic={topic}
												isFiltered={isFiltered}
											/>
										}
										variant='plain'
										sx={{ borderRadius: '0px' }}
										arrow
										placement={!matches ? 'top' : 'left'}
									>
										<Stack
											key={item.group}
											direction={'row'}
											sx={{ lineHeight: 1.3 }}
											alignItems='center'
										>
											{type === 'money' ? (
												<BoxChart
													item={item}
													scale={scale}
													topic={topic}
													isFiltered={isFiltered}
													title={title as string}
													print={print}
												/>
											) : (
												<BoxPctChart
													item={item}
													topic={topic}
													isFiltered={isFiltered}
													title={title as string}
													print={print}
												/>
											)}
											<Typography
												level='label'
												sx={{
													fontSize: '20px',
													fontWeight: 700,
													color: item.color,
													lineHeight: '33.6px'
												}}
											>
												{type === 'money'
													? DOLLAR_FORMATTER(+item.value)
													: +item.value < 0.01 && +item.value > 0
													? formatValue(+item.value)
													: PERCENT_FORMATTER(+item.value)}
											</Typography>
											<Typography
												level='label'
												sx={{
													fontSize: '20px',
													color: item.color,
													lineHeight: '33.6px'
												}}
											>
												&nbsp;
												{item.label}
											</Typography>
										</Stack>
									</UiTooltip>
								))}
							</Box>
						) : null}
					</Box>

					{/* : (
						<Typography
							level='annotation'
							sx={{ mt: '1rem', fontStyle: 'italic' }}
						>
							No data to display
						</Typography>
					)} */}
				</Box>
			</Box>
		</>
	);
};

export default DisaggChart;

interface BoxProps {
	item: { [x: string]: string | number };
	scale: (arg0: number | string) => number;
	topic: string;
	title: string;
	isFiltered: boolean;
	print?: boolean;
}

const BoxChart = ({ item, scale, print = false }: BoxProps) => {
	return (
		<Box
			aria-label='a colored bar indicating the amount of money for a disaggregated group'
			sx={{
				width: '50px',
				height: '12px',
				position: 'relative',
				margin: print ? '1.5rem .5rem 0 2px' : '0 .5rem 0 2px'
			}}
		>
			<Box
				sx={{
					position: 'absolute',
					top: '1px',
					left: 0,
					borderRight: '2px solid black',
					width: '100%',
					height: '8px'
				}}
			></Box>
			<Box
				sx={{
					position: 'absolute',
					top: '4px',
					left: 0,
					borderTop: '2px solid black',
					width: '100%',
					height: '2px'
				}}
			></Box>
			<Box
				sx={{
					position: 'absolute',
					top: 0,
					left: 0,
					border: '1px solid black',
					width: `${scale(item.value)}%`,
					height: '10px',
					backgroundColor: item.color
				}}
			></Box>
		</Box>
	);
};

interface BoxPctProps {
	item: { [x: string]: string | number };
	topic: string;
	title: string;
	isFiltered: boolean;
	print: boolean;
}
const BoxPctChart = ({ item, print = false }: BoxPctProps) => {
	return (
		<Box
			aria-label='a colored bar indicating the percentage of money for a disaggregated group'
			sx={{
				position: 'relative',
				width: '50px',
				height: '12px',
				margin: print ? '1.5rem .5rem 0 2px' : '0 .5rem 0 2px'
			}}
		>
			<Box
				sx={{
					width: '50px',
					height: '12px',
					position: 'absolute',
					top: 0,
					left: 0,
					border: '1px solid #333',
					backgroundColor: 'transparent',
					zIndex: 3
				}}
			></Box>
			<Box
				sx={{
					position: 'absolute',
					top: 0,
					left: 0,
					//border: '1px solid black',
					width: `${(item.value as number) * 100}%`,
					height: '12px',
					backgroundColor: item.color,
					zIndex: 1
				}}
			></Box>
		</Box>
	);
};
